import { useCallback } from 'react';
import useAuthStore from '../store/authStore';

const useBehaviorTracking = () => {
  const { token, user } = useAuthStore();

  const trackEvent = useCallback(async (eventType, eventData) => {
    if (!token || !user) return;

    try {
      const { default: api } = await import('../services/api');
      
      // For now, we'll track events via a simple endpoint
      // In a real implementation, you might have a dedicated tracking endpoint
      await api.post('/users/track-event', {
        event_type: eventType,
        event_data: eventData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.warn('Failed to track event:', error);
      // Don't throw - tracking shouldn't break the user experience
    }
  }, [token, user]);

  const trackCourseClick = useCallback((courseId, courseData = {}) => {
    trackEvent('course_click', {
      course_id: courseId,
      course_title: courseData.title,
      course_category: courseData.category
    });
  }, [trackEvent]);

  const trackJobView = useCallback((jobId, jobData = {}) => {
    trackEvent('job_view', {
      job_id: jobId,
      job_title: jobData.title,
      job_company: jobData.company,
      job_location: jobData.location
    });
  }, [trackEvent]);

  const trackRoadmapGeneration = useCallback((roadmapData) => {
    trackEvent('roadmap_generated', {
      roadmap_type: roadmapData.type,
      target_role: roadmapData.targetRole,
      skills_focused: roadmapData.skills,
      estimated_duration: roadmapData.duration
    });
  }, [trackEvent]);

  const trackSessionStart = useCallback((sessionType = 'learning') => {
    trackEvent('session_start', {
      session_type: sessionType,
      start_time: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackSessionEnd = useCallback((sessionType = 'learning', duration) => {
    trackEvent('session_end', {
      session_type: sessionType,
      duration_minutes: duration,
      end_time: new Date().toISOString()
    });
  }, [trackEvent]);

  const trackRecommendationInteraction = useCallback((recommendationId, interactionType, recommendationData = {}) => {
    trackEvent('recommendation_interaction', {
      recommendation_id: recommendationId,
      interaction_type: interactionType, // 'viewed', 'clicked', 'completed', 'dismissed'
      recommendation_type: recommendationData.type,
      recommendation_title: recommendationData.title
    });
  }, [trackEvent]);

  const trackTopicSearch = useCallback((topic, searchResults = {}) => {
    trackEvent('topic_search', {
      topic: topic,
      results_count: searchResults.count || 0,
      search_type: searchResults.type || 'general'
    });
  }, [trackEvent]);

  const trackSkillAssessment = useCallback((skillName, confidenceLevel, context = {}) => {
    trackEvent('skill_assessment', {
      skill_name: skillName,
      confidence_level: confidenceLevel,
      assessment_context: context.type || 'manual',
      timestamp: new Date().toISOString()
    });
  }, [trackEvent]);

  return {
    trackCourseClick,
    trackJobView,
    trackRoadmapGeneration,
    trackSessionStart,
    trackSessionEnd,
    trackRecommendationInteraction,
    trackTopicSearch,
    trackSkillAssessment,
    trackEvent
  };
};

export default useBehaviorTracking;